// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Promote-to-alarm and "Not an alarm" — the anomaly equivalent of
// AlarmNextStepCard, and the only writes an anomaly supports today.
//
// "Not an alarm" closes the finding with the CLOSE operator event (the
// `closed_loop` machine has a CLOSE cell from its open states). A CLOSED finding
// offers neither action.
//
// Acknowledge and the owner row are AlarmNextStepCard's (finding mode), rendered
// above this card on the anomaly page. Create work order is not offered on a
// finding (ADR-0039).
//
// Promotion changes classification only, leaving alarm_status/assigned_to
// untouched. See docs/upstream-specs/alarm-api.yaml (the `findings` tag).

import React, { useState } from 'react';
import { Button } from '@carbon/react';
import { ArrowUp } from '@carbon/icons-react';
import {
  usePromoteAnomaly,
  useCloseAnomaly,
  promoteErrorMessage,
  isPromoteRetryable,
  closeErrorMessage,
} from '../../hooks/useAnomalyMutation';

interface Props {
  /** The per-occurrence `alarm_id`. A code addresses the rule, not this row. */
  alarmId?: string;
  /** Shown in the confirm copy so the operator sees what they are promoting. */
  alarmCode: string;
  siteId?: string;
  /** The finding's alarm_status. CLOSED means it was already closed as not an alarm. */
  status?: string | null;
  /** Called after a successful promotion, so the page can move the operator on. */
  onPromoted: () => void;
}

export default function AnomalyPromoteCard({
  alarmId,
  alarmCode,
  siteId,
  status,
  onPromoted,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  // The occurrence this card just closed. The status prop only follows once the
  // detail re-reads, and until then (or if that re-read fails) both actions would
  // come back for a finding that is already closed.
  const [closedAlarmId, setClosedAlarmId] = useState<string | null>(null);
  const promote = usePromoteAnomaly();
  const close = useCloseAnomaly();
  const busy = promote.isPending || close.isPending;

  // Closed as "not an alarm": nothing left to do, and promoting a closed finding
  // would only 409 or resurrect it.
  if (status === 'CLOSED' || (!!alarmId && closedAlarmId === alarmId)) {
    return (
      <section className="ald-card ald-next-step">
        <div className="ald-card-title">Closed</div>
        <p className="ald-floor-note">This anomaly was closed as not an alarm.</p>
      </section>
    );
  }

  if (!siteId || !alarmId) {
    return (
      <section className="ald-card ald-next-step">
        <div className="ald-card-title">Promote to alarm</div>
        <p className="ald-proc-empty">
          {siteId
            ? "This anomaly can't be identified, so it can't be promoted from here."
            : "No site context for this anomaly, so it can't be promoted from here."}
        </p>
      </section>
    );
  }

  return (
    <section className="ald-card ald-next-step">
      <div className="ald-card-title">Promote to alarm</div>

      {confirming ? (
        <>
          <p className="ald-floor-note">
            This moves {alarmCode} into the alarms list for everyone at this site. It affects only
            this anomaly, and it can&apos;t be undone from Helios.
          </p>
          <div className="hs-card-row">
            <Button
              kind="danger"
              size="md"
              disabled={busy}
              onClick={() =>
                promote.mutate(
                  { siteId, alarmId, alarmCode },
                  {
                    onSuccess: () => {
                      setConfirming(false);
                      onPromoted();
                    },
                  }
                )
              }
            >
              {promote.isPending ? 'Promoting…' : 'Confirm promotion'}
            </Button>
            <Button
              kind="ghost"
              size="md"
              disabled={promote.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="ald-floor-note">
            Raise this anomaly to a tracked alarm so it can be worked.
          </p>
          <Button
            kind="primary"
            size="md"
            renderIcon={ArrowUp}
            disabled={busy}
            onClick={() => {
              // One confirm at a time, and no error left over from the other action.
              setConfirmingClose(false);
              close.reset();
              setConfirming(true);
            }}
          >
            Promote to alarm
          </Button>
          {confirmingClose ? (
            <>
              <p className="ald-floor-note">
                Close {alarmCode} as not an alarm? It stays in the anomalies list, marked closed,
                and can&apos;t be reopened from Helios.
              </p>
              <div className="hs-card-row">
                <Button
                  kind="danger"
                  size="md"
                  disabled={busy}
                  onClick={() =>
                    close.mutate(
                      { siteId, alarmId, alarmCode },
                      {
                        onSuccess: () => {
                          setConfirmingClose(false);
                          setClosedAlarmId(alarmId);
                        },
                      }
                    )
                  }
                >
                  {close.isPending ? 'Closing…' : 'Confirm close'}
                </Button>
                <Button
                  kind="ghost"
                  size="md"
                  disabled={busy}
                  onClick={() => setConfirmingClose(false)}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <Button
              kind="tertiary"
              size="md"
              disabled={busy}
              onClick={() => {
                promote.reset();
                setConfirmingClose(true);
              }}
            >
              Not an alarm
            </Button>
          )}
        </>
      )}

      {close.error && (
        <p className="ald-wo-cmms-error" role="alert">
          {closeErrorMessage(close.error)}
        </p>
      )}

      {promote.error && (
        <p className="ald-wo-cmms-error" role="alert">
          {promoteErrorMessage(promote.error)}
          {isPromoteRetryable(promote.error) && (
            <>
              {' '}
              <Button
                kind="ghost"
                size="sm"
                disabled={promote.isPending}
                onClick={() => promote.mutate({ siteId, alarmId, alarmCode })}
              >
                Retry
              </Button>
            </>
          )}
        </p>
      )}
    </section>
  );
}
